import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionTabs,
  AppIcon,
  ConfirmDelete,
  ContentNugget,
  addHttpIfMissing,
  mapNuggetToCardData,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  DetailHeader,
  detailHeaderChipSx,
  detailHeaderCompletePillSx,
} from '@backrs_ui/grow-ui/src/components/DetailHeader/DetailHeader';
import { ContentNuggetChip } from '@backrs_ui/grow-ui/src/components/Nuggets/ContentNuggetChip';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import {
  PRIORITY_TASK_STATUS_CODES,
  PRIORITY_TYPES,
  PriorityTask,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Button, Chip, SxProps, Theme, Typography } from '@mui/material';

import { useTaskLinkNugget } from '../hooks/tasks.hooks';
import { transformTaskContentsToNuggets } from '../utils/tasks.utils';

const STRINGS = appStrings.modules.priorities.taskPage;
const TASK_CARD_STRINGS = appStrings.modules.priorities.taskCard;

type PlaybookTaskRowProps = Readonly<{
  task: PriorityTask;
  isReorderMode?: boolean;
  /** Each action renders only when its handler is given, so a read-only row omits them. */
  onOpen?: (task: PriorityTask) => void;
  onToggleComplete?: (task: PriorityTask) => Promise<void>;
  onDuplicate?: (task: PriorityTask) => void;
  onDelete?: (task: PriorityTask) => Promise<void>;
  onEdit?: (task: PriorityTask) => void;
}>;

export const PlaybookTaskRow = ({
  task,
  isReorderMode = false,
  onOpen,
  onToggleComplete,
  onDuplicate,
  onDelete,
  onEdit,
}: PlaybookTaskRowProps) => {
  const navigate = useNavigate();
  const { getStickerCard } = useToolCardById({ navigate });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const completed = task.status_code === PRIORITY_TASK_STATUS_CODES.COMPLETED;
  const nuggets = transformTaskContentsToNuggets(task);

  const linkNugget = useTaskLinkNugget(task);
  const thumbnailUrl = nuggets
    .map((nugget) => mapNuggetToCardData({ nugget }).imageUrl)
    .find(Boolean);

  const chipNuggets = linkNugget ? [...nuggets, linkNugget] : nuggets;

  const handleNuggetClick = (nugget: ContentNugget) => {
    if (nugget.variant === 'tool' && nugget.tool_info) {
      const stickerCard = getStickerCard(nugget.tool_info.tool_id as StickerCardId);

      if (stickerCard?.onClick) {
        stickerCard.onClick();

        return;
      }
    }

    handlePanelNuggetClick(nugget, navigate);
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      await onDelete(task);
      setConfirmDeleteOpen(false);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  return (
    <>
      <ConfirmDelete
        openDialog={confirmDeleteOpen}
        setOpenDialog={setConfirmDeleteOpen}
        headerText={TASK_CARD_STRINGS.deleteTask}
        bodyText={TASK_CARD_STRINGS.deleteTaskConfirmBody}
        handleDelete={handleDelete}
      />

      <DetailHeader
        id={`playbook-task-row-${task.id}`}
        // Reordering marks every row as grabbable, the same way the drawer's cards do
        borderColor={isReorderMode ? 'Brand/300' : undefined}
        onClick={isReorderMode || !onOpen ? undefined : () => onOpen(task)}
        leading={
          thumbnailUrl ? (
            <Box component="img" src={thumbnailUrl} alt="" sx={thumbnailSx} />
          ) : (
            <Box sx={thumbnailPlaceholderSx} aria-hidden>
              <AppIcon
                iconName="ClipboardPlus"
                diameter="24px"
                color="Colors/Foreground/fg-quaternary"
              />
            </Box>
          )
        }
        title={task.title}
        titleAdornment={
          task.priority ? (
            <Chip
              id={`playbook-task-priority-chip-${task.id}`}
              size="sm"
              variant="badge"
              label={`${PRIORITY_TYPES[task.priority]} ${STRINGS.priority}`}
              sx={detailHeaderChipSx}
            />
          ) : undefined
        }
        subtitleContent={
          task.due_date || chipNuggets.length > 0 ? (
            <Box sx={subtitleStackSx}>
              {task.due_date && (
                <Typography variant="text-sm-regular" sx={dueDateTextSx}>
                  {STRINGS.due}{' '}
                  {new Date(task.due_date).toLocaleDateString('en-US', {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                  })}
                </Typography>
              )}
              {chipNuggets.length > 0 && (
                <Box sx={chipRowSx} onClick={(event) => event.stopPropagation()}>
                  {nuggets.map((nugget) => (
                    <ContentNuggetChip
                      key={nugget.id}
                      id={`playbook-task-nugget-chip-${task.id}-${nugget.id}`}
                      nugget={nugget}
                      onClick={() => handleNuggetClick(nugget)}
                    />
                  ))}
                  {linkNugget && (
                    <ContentNuggetChip
                      id={`playbook-task-link-chip-${task.id}`}
                      nugget={linkNugget}
                      onClick={() =>
                        window.open(addHttpIfMissing(task.content_url || ''), '_blank')
                      }
                    />
                  )}
                </Box>
              )}
            </Box>
          ) : undefined
        }
        actions={
          <>
            {onToggleComplete && (
              <Button
                id={`playbook-task-complete-toggle-${task.id}`}
                hierarchy="Secondary color"
                size="md"
                icon="Leading"
                onClick={() => onToggleComplete(task)}
                aria-label={STRINGS.toggleCompleteAria}
                aria-pressed={completed}
                startIcon={
                  <AppIcon
                    iconName={completed ? 'CheckCircle' : 'ClockFastForward'}
                    diameter="20px"
                    color="currentColor"
                  />
                }
                sx={completed ? detailHeaderCompletePillSx : undefined}
              >
                {completed ? STRINGS.complete : STRINGS.incomplete}
              </Button>
            )}
          </>
        }
        secondaryActions={
          onDuplicate || onDelete || onEdit ? (
            <ActionTabs
              borderColor="Colors/Border/border-primary"
              actions={[
                ...(onDuplicate
                  ? [
                      {
                        id: `playbook-task-duplicate-button-${task.id}`,
                        icon: 'Copy04' as const,
                        onClick: () => onDuplicate(task),
                        tooltipHoverText: STRINGS.duplicate,
                      },
                    ]
                  : []),
                ...(onDelete
                  ? [
                      {
                        id: `playbook-task-delete-button-${task.id}`,
                        icon: 'Trash01' as const,
                        onClick: () => setConfirmDeleteOpen(true),
                        tooltipHoverText: STRINGS.delete,
                      },
                    ]
                  : []),
                ...(onEdit
                  ? [
                      {
                        id: `playbook-task-edit-button-${task.id}`,
                        icon: 'Pencil01' as const,
                        onClick: () => onEdit(task),
                        tooltipHoverText: STRINGS.edit,
                      },
                    ]
                  : []),
              ]}
            />
          ) : undefined
        }
      />
    </>
  );
};

const thumbnailSx: SxProps<Theme> = ({ backrs }) => ({
  width: '64px',
  height: '64px',
  flexShrink: 0,
  objectFit: 'cover',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const thumbnailPlaceholderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '64px',
  height: '64px',
  flexShrink: 0,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const dueDateTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const subtitleStackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
});

const chipRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});
